import React, { useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { mockSchools } from '../data/schools';
import { SchoolListingCard } from '../components/SchoolListingCard';
import { FormData } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
import { usePageMeta } from '../hooks/usePageMeta';
export function ResultsPage() {
  usePageMeta('Your Matched Schools | OnlineCollegeGuide', 'View your personalized list of matched online colleges and universities based on your degree preferences and goals.', {
    ogImage: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const location = useLocation();
  const navigate = useNavigate();
  // Get form data from router state
  const formData = location.state?.formData as FormData;
  useEffect(() => {
    window.scrollTo(0, 0);
    // If accessed directly without form data, redirect home
    if (!formData) {
      navigate('/');
    }
  }, [formData, navigate]);
  if (!formData) return null;
  // Resolve labels for display
  const degreeName = degreeInterestOptions.find(d => d.value === formData.degreeInterest)?.type || '';
  const focusName = focusOptions.find(f => f.value === formData.areaOfStudy)?.type || '';
  const specName = specializationOptions.find(s => s.value === formData.specialization)?.type || '';
  return <main className="flex-grow bg-light pb-16" data-id="element-1118">
      {/* Success Header */}
      <div className="bg-navy text-white py-12 border-b-4 border-gold" data-id="element-1119">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1120">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 text-green-400 mb-6" data-id="element-1121">
            <CheckCircleIcon className="w-10 h-10" data-id="element-1122" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1123">
            Success, {formData.firstName}! Here are your matches.
          </h1>
          <p className="text-lg text-gray-300" data-id="element-1124">
            Based on your interest in{' '}
            <span className="text-white font-semibold" data-id="element-1125">
              {specName || focusName}
            </span>
            , we've found these accredited schools that match your profile.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1126">
        {/* User Selections Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8 flex flex-wrap items-center justify-between gap-4 text-sm" data-id="element-1127">
          <div className="flex items-center text-gray-600" data-id="element-1128">
            <span className="font-semibold text-navy mr-2" data-id="element-1129">Degree Level:</span>
            {degreeName}
          </div>
          <div className="flex items-center text-gray-600" data-id="element-1130">
            <span className="font-semibold text-navy mr-2" data-id="element-1131">Current Ed:</span>
            {formData.currentEducation}
          </div>
          <div className="flex items-center text-gray-600" data-id="element-1132">
            <span className="font-semibold text-navy mr-2" data-id="element-1133">Zip:</span>
            {formData.zipCode}
          </div>
          <Link to="/" className="text-crimson hover:text-crimson-dark font-medium flex items-center" data-id="element-1134">
            <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1135" />
            Edit Search
          </Link>
        </div>

        {/* Results List */}
        <div className="space-y-6" data-id="element-1136">
          <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1137">
            Recommended Programs For You
          </h2>

          {/* Display mock schools as results */}
          {mockSchools.map(school => <SchoolListingCard key={school.id} school={school} onSelect={id => alert(`In a real app, this would redirect to the school's landing page or open a lead submission modal for ${id}.`)} data-id="element-1138" />)}
        </div>

        {/* Next Steps / Email Reminder */}
        <div className="mt-12 bg-warm rounded-xl p-8 border border-gold/30 text-center" data-id="element-1139">
          <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1140">Check Your Inbox</h3>
          <p className="text-gray-600 mb-6" data-id="element-1141">
            We've sent a copy of these results to{' '}
            <strong data-id="element-1142">{formData.email}</strong>. Admissions representatives from
            your matched schools may reach out to provide more information.
          </p>
          <Link to="/" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-navy bg-white hover:bg-gray-50 border-gray-300 shadow-sm transition-colors" data-id="element-1143">
            Explore More Degrees
          </Link>
        </div>
      </div>
    </main>;
}